import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog, type ConfirmDialogProps } from './Modal';

export type ConfirmOptions = Pick<ConfirmDialogProps, 'title' | 'message' | 'confirmLabel' | 'cancelLabel' | 'tone'>;

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

/** useConfirm() を使えるようにする（App 直下に 1 つ配置済み） */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    resolver.current?.(false);
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog open={!!opts} title={opts?.title ?? ''} {...opts} onConfirm={() => settle(true)} onCancel={() => settle(false)} />
    </ConfirmContext.Provider>
  );
}

/**
 * 確認ダイアログを Promise で開く。
 * @example
 *   const confirm = useConfirm();
 *   if (await confirm({ title: '削除しますか？', tone: 'danger', confirmLabel: '削除する' })) { ... }
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm は ConfirmProvider の内側で使用してください');
  return ctx;
}
